import type { Pt } from "@tasduvar/engine";
import { LAYERS, textWidth, wrapLines, type SheetLayout, type SheetPrim } from "../../export/dxf";
import { dimGeom } from "../../export/dxf/edits";

/**
 * Pafta önizlemesi / baskısı: `buildSheet` öğelerini SVG'ye çizer (DXF ile aynı liste → ekranda görülen = DXF'teki).
 * Koordinat: x = model x (m), y = −model y (SVG aşağı pozitif). `mode`: ekran (renkli, ince çizgiler ekranda sabit kalınlık)
 * ya da baskı (siyah, çizgi kalınlığı kâğıtta gerçek mm).
 */
const ACI: Record<number, string> = { 1: "#C81E1E", 2: "#A87908", 3: "#1E7B34", 4: "#0E8A9A", 5: "#1F5FBF", 6: "#9B2FA8", 7: "#1B1E22", 8: "#7C8289" };
const LAYER = new Map<string, { color: number; lineweight: number; ltype?: string }>(LAYERS.map((l) => [l.name, l]));
const DASH: Record<string, number[]> = { DASHED: [1.25, 0.625], HIDDEN: [0.625, 0.3125], DASHDOT: [1.25, 0.3, 0.1, 0.3] };

export interface SheetSvgProps {
  layout: SheetLayout;
  mode?: "screen" | "print";
  /** Blok sürükleme: fare aşağıda blok kimliğiyle çağrılır (ekran kipi) */
  onBlockPointerDown?: (id: string, e: React.PointerEvent<SVGRectElement>) => void;
  selected?: string | null;
  className?: string;
  viewBox?: string;
  svgRef?: React.Ref<SVGSVGElement>;
  /** Başka bir SVG'nin içinde (Çizim paftası pan/zoom) → <g> olarak çizilir */
  nested?: boolean;
  /** Tek renk (seçim vurgusu / taşıma önizlemesi); kâğıt ve desen tanımları çizilmez */
  tint?: string;
}

export function SheetSvg({ layout, mode = "screen", onBlockPointerDown, selected, className, viewBox, svgRef, nested, tint }: SheetSvgProps): JSX.Element {
  const f = layout.frame;
  const pad = layout.th * 2;
  const vb = viewBox ?? `${f.minX - pad} ${-f.maxY - pad} ${f.maxX - f.minX + 2 * pad} ${f.maxY - f.minY + 2 * pad}`;
  const print = mode === "print";
  // Kâğıtta 1 mm = scale/1000 m
  const mm = layout.scale / 1000;
  const userColor = new Map(layout.layers.map((l) => [l.name, l.color]));
  const color = (layer: string, c?: number): string => (tint ?? (print ? "#000" : ACI[c ?? LAYER.get(layer)?.color ?? userColor.get(layer) ?? 7] ?? "#1B1E22"));
  const stroke = (layer: string, lw?: number): { strokeWidth: number; vectorEffect?: "non-scaling-stroke" } => {
    const w = (lw ?? LAYER.get(layer)?.lineweight ?? 18) / 100;   // mm
    return print ? { strokeWidth: Math.max(w, 0.09) * mm } : { strokeWidth: Math.max(0.6, w * 2.2), vectorEffect: "non-scaling-stroke" };
  };
  const dash = (layer: string, ltype?: string): string | undefined => { const d = DASH[ltype ?? LAYER.get(layer)?.ltype ?? ""]; return d ? d.map((v) => v * 0.2 * layout.scale / 100 * (print ? 1 : 1)).join(" ") : undefined; };
  const P = (p: Pt): string => `${p.x},${-p.y}`;
  const Root = nested ? "g" : "svg";
  const rootProps = nested ? { style: { fontFamily: '"Red Hat Mono", ui-monospace, monospace' } } : { ref: svgRef, xmlns: "http://www.w3.org/2000/svg", viewBox: vb, className, style: { fontFamily: '"Red Hat Mono", ui-monospace, monospace' }, ...(print ? { width: `${layout.paperMm.w}mm`, height: `${layout.paperMm.h}mm` } : {}) };
  return (
    <Root {...(rootProps as object)}>
      {tint ? null : <><defs>
        <pattern id="sheet-hatch" patternUnits="userSpaceOnUse" width={layout.th * 1.2} height={layout.th * 1.2} patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2={layout.th * 1.2} stroke={print ? "#000" : "#9AA0A6"} strokeWidth={layout.th * 0.06} />
        </pattern>
      </defs>
      <rect x={f.minX} y={-f.maxY} width={f.maxX - f.minX} height={f.maxY - f.minY} fill="#fff" /></>}
      {layout.prims.map((e, i) => <g key={e.key ?? i} data-key={tint ? undefined : e.key}><Prim e={e} P={P} color={color} stroke={stroke} dash={dash} layout={layout} tint={!!tint} /></g>)}
      {onBlockPointerDown ? layout.blocks.filter((b) => !b.hidden).map((b) => {
        const x = b.box.minX + b.offset.x, y = b.box.minY + b.offset.y, w = b.box.maxX - b.box.minX, h = b.box.maxY - b.box.minY;
        return (
          <g key={b.id} data-block={b.id}>
            <rect x={x - layout.th} y={-(y + h) - layout.th} width={w + 2 * layout.th} height={h + 2 * layout.th} fill="transparent"
              stroke={selected === b.id ? "#2F6FDB" : "transparent"} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" strokeWidth={1.2}
              className="cursor-move hover:stroke-[#2F6FDB]" onPointerDown={(ev) => onBlockPointerDown(b.id, ev)} aria-label={b.label} role="button" />
          </g>
        );
      }) : null}
    </Root>
  );
}

function Prim({ e, P, color, stroke, dash, layout, tint }: { e: SheetPrim; P: (p: Pt) => string; color: (l: string, c?: number) => string; stroke: (l: string, lw?: number) => object; dash: (l: string, t?: string) => string | undefined; layout: SheetLayout; tint?: boolean }): JSX.Element | null {
  switch (e.k) {
    case "line": return <line x1={e.a.x} y1={-e.a.y} x2={e.b.x} y2={-e.b.y} stroke={color(e.layer, e.extra?.color)} strokeDasharray={dash(e.layer, e.extra?.ltype)} {...stroke(e.layer, e.extra?.lineweight)} />;
    case "poly": { const C = e.closed ? "polygon" : "polyline"; return <C points={e.pts.map(P).join(" ")} fill="none" stroke={color(e.layer, e.extra?.color)} strokeDasharray={dash(e.layer, e.extra?.ltype)} {...stroke(e.layer, e.extra?.lineweight)} />; }
    case "circle": return <circle cx={e.c.x} cy={-e.c.y} r={e.r} fill="none" stroke={color(e.layer)} {...stroke(e.layer)} />;
    case "solid": return <polygon points={e.pts.map(P).join(" ")} fill={color(e.layer)} />;
    case "hatch": return <path d={e.loops.map((L) => `M${L.map(P).join("L")}Z`).join("")} fillRule="evenodd" fill={e.pattern && !tint ? "url(#sheet-hatch)" : color("TARAMA", e.color)} fillOpacity={tint ? 0.18 : e.pattern ? 1 : 0.35} />;
    case "text": return <SvgText p={e.p} value={e.value} h={e.opts.h} rot={e.opts.rotation ?? 0} ha={e.opts.halign ?? 0} va={e.opts.valign ?? 0} fill={color(e.layer, e.opts.color)} />;
    case "mtext": return <g fill={color(e.layer)}>{wrapLines(e.lines, e.width, e.h).map((line, i) => <text key={i} x={e.p.x} y={-e.p.y + e.h * (1 + i * 1.5)} fontSize={e.h}>{line}</text>)}</g>;
    case "dim": return <Dim e={e} layout={layout} color={color(e.layer ?? "OLCU")} stroke={stroke(e.layer ?? "OLCU")} />;
  }
}

function SvgText({ p, value, h, rot, ha, va, fill }: { p: Pt; value: string; h: number; rot: number; ha: number; va: number; fill: string }): JSX.Element {
  const underline = value.startsWith("%%U");
  const v = value.replace(/%%U/g, "").replace(/%%c/gi, "Ø").replace(/%%d/gi, "°");
  const anchor = ha === 1 ? "middle" : ha === 2 ? "end" : "start";
  const baseline = va === 2 ? "central" : va === 3 ? "hanging" : "alphabetic";
  return <text x={p.x} y={-p.y} fontSize={h} fill={fill} textAnchor={anchor} dominantBaseline={baseline} textDecoration={underline ? "underline" : undefined} transform={rot ? `rotate(${-rot} ${p.x} ${-p.y})` : undefined} textLength={v.length > 2 ? textWidth(v, h) : undefined} lengthAdjust="spacingAndGlyphs">{v}</text>;
}

/** DXF dimLinear ile aynı geometri: uzatma çizgileri, ölçü çizgisi, oklar, metin */
function Dim({ e, layout, color, stroke }: { e: Extract<SheetPrim, { k: "dim" }>; layout: SheetLayout; color: string; stroke: object }): JSX.Element {
  const { vertical, p1, p2, measure } = dimGeom(e);
  const u = layout.unit === "cm" ? 100 : 1;
  const label = e.text ?? (measure * u).toFixed(layout.dimDecimals).replace(".", ",");
  const th = layout.dimTh, ar = layout.dimArrow, gap = th * 0.35;
  const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  const tp = vertical ? { x: mid.x - gap, y: mid.y } : { x: mid.x, y: mid.y + gap };
  const dir = vertical ? { x: 0, y: Math.sign(p2.y - p1.y) || 1 } : { x: Math.sign(p2.x - p1.x) || 1, y: 0 };
  const arrow = (tip: Pt, d: Pt): string => { const base = { x: tip.x + d.x * ar, y: tip.y + d.y * ar }, n = { x: -d.y, y: d.x }, w = ar / 6; return `${tip.x},${-tip.y} ${base.x + n.x * w},${-(base.y + n.y * w)} ${base.x - n.x * w},${-(base.y - n.y * w)}`; };
  const inside = layout.dimTick || measure > 2.5 * ar;
  // Eğik çizgi (tick): 45° kısa çizgi, uzunluk = ok boyu (DXF dimLinear ile aynı)
  const tick = (p: Pt): JSX.Element => { const h = ar / 2, n = { x: -dir.y, y: dir.x }, v = { x: (dir.x + n.x) * h * Math.SQRT1_2, y: (dir.y + n.y) * h * Math.SQRT1_2 }; return <line x1={p.x - v.x} y1={-(p.y - v.y)} x2={p.x + v.x} y2={-(p.y + v.y)} stroke={color} {...stroke} />; };
  return (
    <g>
      <line x1={e.a.x} y1={-e.a.y} x2={p1.x} y2={-p1.y} stroke={color} {...stroke} />
      <line x1={e.b.x} y1={-e.b.y} x2={p2.x} y2={-p2.y} stroke={color} {...stroke} />
      <line x1={p1.x} y1={-p1.y} x2={p2.x} y2={-p2.y} stroke={color} {...stroke} />
      {layout.dimTick ? <>{tick(p1)}{tick(p2)}</> : <>
        <polygon points={arrow(p1, inside ? dir : { x: -dir.x, y: -dir.y })} fill={color} />
        <polygon points={arrow(p2, inside ? { x: -dir.x, y: -dir.y } : dir)} fill={color} />
      </>}
      <SvgText p={tp} value={label} h={th} rot={vertical ? 90 : 0} ha={1} va={0} fill={color} />
    </g>
  );
}
